/** Roster domain types. `RosterPerson.name` is the join key everywhere; go through the helpers rather than assuming a key is a name. */

import type { HiddenProjects } from './hiddenProjects'
import type { RowLayout } from './rowLayout'

export interface RosterRole {
  mn: string
  en: string
}

/** A schedule code (one plan-grid cell). `cat`, not `project`, drives the schedule: travel and leave days have no project. */
export interface RosterCode {
  code: string
  project: string
  label: string
  label_en?: string
  cat: 'work' | 'travel' | 'leave'
  colour: string
  act?: string
}

export interface RosterPerson {
  id?: string
  name: string
  label?: string
  role: string
  roles: string[]
  company: string
  /** Served, ignored on save: the HR-linked issuer identity per language. */
  companyNames?: { en: string; mn: string; ru: string }
  /** Served, ignored on save: present only for an external worker; '' when HR has no firm. */
  externalCompany?: string
  home: string
  kind: string
  /** Coarse half of a pair: the day they left is `meta.leftOn[name]`; read both through `employment.ts`. */
  left?: boolean
  /** First day HR says this person is gone (ISO) or null; read through `leftOn` in `employment.ts`. Ignored on save. */
  hr_left_on?: string | null
  /** Manager's draft only, ignored on save: the runs of dates (both ends inclusive) under a Daily+Hourly rate; read through `contractChangedSince`. */
  contractorLog?: { since: string; until: string | null }[]
  /** Manager view only. Only `full` means can sign in and file; `none`, `unknown` and absent all mean cannot (`needsLoginChip`). */
  login?: 'none' | 'full' | 'unknown'
  employment?: 'full_time' | 'contractor' | 'external' | ''
  memberOf?: string[]
  rosterRole?: 'manager' | 'staff' | 'person' | ''

  // Not served by the API (`reg` is a national ID); kept optional to match the source document.
  reg?: string
  phone?: string
  sos?: string
  mail?: string

  /** Corrections to the derived Projects column, not the list itself: only the difference is stored so a newly planned project is never frozen out. */
  projAdd?: string[]
  /** Spelled as the API serves it (`proj_remove`); another spelling breaks removals. */
  projRem?: string[]
  projects?: string[]
}

export interface ActualEntry {
  project?: string
  type: string
  value?: number
  lodge?: boolean
  meals?: string
  comment?: string
}

/** One day of the `actual` grid. `source` says whether the guest log or the roster supplied it; roster-sourced days carry no entries. */
export interface ActualDetail {
  codes: string[]
  entries: ActualEntry[]
  source: 'guest-log' | 'roster'
  taskCount: number
}

type DayCodes = Record<string, string>
export type Grid = Record<string, DayCodes>

export type Requirements = Record<string, Record<string, number>>

/** Head-counts are seasonal: always ask per day via `need()`, never read `req` directly. */
interface RequirementPeriod {
  id: string
  from: string
  to: string
  req: Requirements
}


export interface HseItem {
  id: string
  name: string
  months: number
  cat: 'ppe' | 'cert' | 'size'
  type: 'date' | 'num' | 'text' | 'check' | 'yesno'
}

/** The union is load-bearing: writes coerce to the column's `type` so pasting behaves like typing. */
export type HseValue = string | number | boolean | null

export interface HseState {
  items: HseItem[]
  rec: Record<string, Record<string, HseValue>>
}

export type HseStatus = 'ok' | 'due' | 'expired' | 'none'


export interface Expense {
  id: string
  date: string
  project: string
  category: string
  item: string
  amount: number
  pay: string
  who: string
  note: string
}

export interface ExpenseState {
  cats: string[]
  pays: string[]
  list: Expense[]
}


/** Only overrides are stored; direction and departure derive from the schedule (`busTrips`). Keyed `name|travelDay`. */
export interface BusOverride {
  dir?: 'in' | 'out'
  hour?: number
  booked?: boolean
  off?: boolean
  seat?: number | ''
  date?: string
  note?: string
}

interface BusState {
  rows: Record<string, BusOverride>
  proj: string
}


/** Sampling is string-typed: empty means not done, not zero metres. */
export interface PerfEntry {
  id: string
  date: string
  proj: string
  hole: string
  status: string
  geo: string
  tech: string
  clF: number | ''
  clT: number | ''
  clM: number | ''
  gtF: number | ''
  gtT: number | ''
  gtM: number | ''
  cpF: number | ''
  cpT: number | ''
  cpP: number | ''
  smF: number | ''
  smT: number | ''
  smM: number | ''
  note: string
}

interface PerfState {
  list: PerfEntry[]
  mailTo: Record<string, string>
}


interface RosterMeta {
  start: string
  end: string
  maxRun: number
  freeWin: number
  /** Per-person longest-rotation overrides `{ name: days }`, keyed by name in `meta`; renaming a person orphans theirs. */
  personRun?: Record<string, number>
  /** `{ name: 'YYYY-MM-DD' }`: a date, not a flag, because leaving is not retroactive (see `employment.ts`). */
  leftOn?: Record<string, string>
  /** The last day the Plan was published to; only the server writes it. */
  plannedUntil?: string | null
  /** Days off owed between rotations, travel days not counted; optional and off at 0. */
  minRest?: number
}

export interface FilingScope {
  unrestricted: boolean
  project: string | null
  reason: string
  names: string[]
  projects?: string[]
  override?: boolean
}

export interface RosterDb {
  meta: RosterMeta
  /** Shared Schedule row order and hidden set; only a roster manager saves it. */
  rowLayout?: RowLayout
  /** Projects hidden from the schedule for every reader; only `PUT /api/roster/hidden-projects` writes it, a document save keeps the stored one. */
  hiddenProj?: HiddenProjects
  hiddenPeople?: HiddenProjects
  projects: string[]
  projectNames?: Record<string, { mn: string; en: string }>
  companies: string[]
  roles: RosterRole[]
  codes: RosterCode[]
  people: RosterPerson[]
  plan: Grid
  /** The published Plan; only the server's Publish and Clean write it. `plan` is the shared Draft. */
  planBase?: Grid
  /** The Plan's own stored T / DT project, by person and day. */
  planBaseProject?: Record<string, Record<string, string>>
  /** DERIVED from the guest log, never saved back. Edit through `POST /api/roster/shifts/{id}`. */
  actual: Grid
  actualDetail?: Record<string, Record<string, ActualDetail>>
  planProject?: Record<string, Record<string, string>>
  /** Server-derived from the audit log, manager view only; `code` is what that writer left in the cell. */
  plannedBy?: Record<string, Record<string, PlannedBy>>
  /** Resolved by the server; not derivable client-side, a guess would offer edits the API then refuses. */
  filing?: FilingScope
  view?: 'draft' | 'published'
  /** The shared Draft on a reader's copy, whose `plan` holds the published days; never saved. */
  draftPlan?: Grid
  draftPlanProject?: Record<string, Record<string, string>>
  staff?: Record<string, string[]>
  eligible?: Record<string, string[]>
  managers?: string[]
  req: Requirements
  reqP: RequirementPeriod[]
  hse: HseState
  exp: ExpenseState
  bus: BusState
  perf: PerfState
}

/** `GET /api/roster/me/project`; a day on a project outside `projects` reads `"~"` (`OUT_OF_SCOPE`). */
export interface ProjectRoster {
  projects: string[]
  from: string
  to: string
  codes: { code: string; label: string; colour: string; cat: string; project: string }[]
  people: { name: string; label?: string; role: string; home: string }[]
  days: Record<string, Record<string, string>>
  reason: string
  /** Members' leave by name. Deliberately no `type` or person id: a confined reader sees only that it is leave. */
  leave?: ProjectLeaveRow[]
  projectNames?: Record<string, { mn: string; en: string }>
}

interface ProjectLeaveRow {
  name: string
  start: string
  end: string
  state: 'approved' | 'pending'
  halfDay: boolean
}

export const OUT_OF_SCOPE = '~'

interface PlannedBy {
  id: string
  name: string
  code: string
}
